import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { SwipeMatchLogo } from '../components/SwipeMatchLogo';
import { 
  ChevronDown, Search, HelpCircle, Heart, Home, MessageSquare, 
  Shield, CreditCard, User, Zap, Crown, ArrowRight, Sparkles,
  CheckCircle, Clock, Star, Mail, Phone
} from 'lucide-react';
import { Input } from '../components/ui/input';

const FAQPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [navigatingToPremium, setNavigatingToPremium] = useState(false);

  // Handle navigation to premium with loading animation
  const handleNavigateToPremium = async () => {
    setNavigatingToPremium(true);
    await new Promise(resolve => setTimeout(resolve, 1500));
    navigate('/premium');
  };

  // Loading overlay for premium navigation
  if (navigatingToPremium) {
    return (
      <div className="fixed inset-0 bg-gradient-to-br from-[#1F3A5F] to-[#0D2137] flex items-center justify-center z-50">
        <div className="text-center">
          <div className="mb-6 animate-pulse">
            <SwipeMatchLogo size="large" variant="white" className="justify-center" />
          </div>
          <div className="flex flex-col items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce"></div>
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.15s'}}></div>
              <div className="w-3 h-3 bg-[#2E7D32] rounded-full animate-bounce" style={{animationDelay: '0.3s'}}></div>
            </div>
            <p className="text-white/80 text-sm font-medium">
              {language === 'nl' ? 'Premium opties laden...' : 'Loading premium options...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const categories = [
    { id: 'all', label: language === 'nl' ? 'Alles' : 'All', icon: HelpCircle },
    { id: 'subscription', label: language === 'nl' ? 'Gratis platform' : 'Free platform', icon: CreditCard },
    { id: 'swiping', label: language === 'nl' ? 'Swipen' : 'Swiping', icon: Heart },
    { id: 'profile', label: language === 'nl' ? 'Profiel' : 'Profile', icon: User },
    { id: 'landlords', label: language === 'nl' ? 'Verhuurders' : 'Landlords', icon: Home },
    { id: 'safety', label: language === 'nl' ? 'Veiligheid' : 'Safety', icon: Shield },
  ];

  const faqs = [
    {
      category: 'subscription',
      categoryLabel: language === 'nl' ? 'Gratis platform' : 'Free platform',
      icon: CreditCard,
      questions: [
        {
          q: language === 'nl' ? 'Wat is Swipe je woning?' : 'What is Swipe je woning?',
          a: language === 'nl' 
            ? 'Swipe je woning is de datingsite voor woningen - helemaal GRATIS! Je swipet door woningen: naar links als je niet geïnteresseerd bent, naar rechts als je de woning leuk vindt. Bij een "like" wordt de woning opgeslagen in je dashboard waar je direct kunt chatten met de verhuurder.'
            : 'Swipe je woning is the dating site for housing - completely FREE! You swipe through properties: left if you\'re not interested, right if you like it. On a "like" the property is saved to your dashboard where you can chat directly with the landlord.',
          popular: true
        },
        {
          q: language === 'nl' ? 'Is Swipe je woning echt gratis?' : 'Is Swipe je woning really free?',
          a: language === 'nl'
            ? 'Ja, 100% gratis! Wij geloven dat iedereen toegang moet hebben tot goede woningen. Daarom is ons platform helemaal gratis - onbeperkt swipen, direct chatten met verhuurders, geen verborgen kosten.'
            : 'Yes, 100% free! We believe everyone deserves access to good housing. That\'s why our platform is completely free - unlimited swiping, direct chat with landlords, no hidden costs.',
          popular: true
        },
        {
          q: language === 'nl' ? 'Waarom is het gratis?' : 'Why is it free?',
          a: language === 'nl'
            ? 'Wij willen zoveel mogelijk mensen helpen een woning te vinden. De woningmarkt is al moeilijk genoeg - wij vragen geen geld van woningzoekers. Ons doel is om de beste match te maken tussen huurders en verhuurders.'
            : 'We want to help as many people as possible find a home. The housing market is tough enough - we don\'t charge home seekers. Our goal is to make the best match between tenants and landlords.'
        },
        {
          q: language === 'nl' ? 'Zijn er verborgen kosten?' : 'Are there hidden costs?',
          a: language === 'nl'
            ? 'Nee! Swipe je woning is en blijft gratis. Je kunt onbeperkt swipen, chatten met verhuurders, en al je matches bekijken zonder ooit te hoeven betalen.'
            : 'No! Swipe je woning is and will stay free. You can swipe unlimited, chat with landlords, and view all your matches without ever having to pay.'
        }
      ]
    },
    {
      category: 'swiping',
      categoryLabel: language === 'nl' ? 'Swipen & Matchen' : 'Swiping & Matching',
      icon: Heart,
      questions: [
        {
          q: language === 'nl' ? 'Hoe werkt het swipen?' : 'How does swiping work?',
          a: language === 'nl'
            ? 'Super simpel! Stel eerst je profiel en voorkeuren in. Daarna krijg je woningen te zien die bij je passen. Swipe naar links (❌) voor "nee", naar rechts (❤️) voor "ja". Bij een match kun je direct contact opnemen met de verhuurder.'
            : 'Super simple! First set up your profile and preferences. Then you\'ll see properties that match. Swipe left (❌) for "no", right (❤️) for "yes". On a match, you can contact the landlord directly.',
          popular: true
        },
        {
          q: language === 'nl' ? 'Wat is een "match"?' : 'What is a "match"?',
          a: language === 'nl'
            ? 'Wanneer je een woning liked, wordt je bericht naar de verhuurder gestuurd. Als de verhuurder interesse heeft in jouw profiel, heb je een match! Je kunt dan direct chatten en een bezichtiging plannen.'
            : 'When you like a property, your message is sent to the landlord. If the landlord is interested in your profile, you have a match! You can then chat directly and schedule a viewing.'
        },
        {
          q: language === 'nl' ? 'Kan ik een swipe ongedaan maken?' : 'Can I undo a swipe?',
          a: language === 'nl'
            ? 'Ja! Je kunt je laatste swipe ongedaan maken. Zo mis je nooit een droomwoning als je per ongeluk de verkeerde kant op swipet.'
            : 'Yes! You can undo your last swipe. So you never miss a dream home if you accidentally swipe the wrong way.'
        },
        {
          q: language === 'nl' ? 'Hoeveel woningen kan ik swipen?' : 'How many properties can I swipe?',
          a: language === 'nl'
            ? 'ONBEPERKT! Je kunt zoveel swipen als je wilt - helemaal gratis. Bekijk alle woningen en vind je droommatch.'
            : 'UNLIMITED! You can swipe as much as you want - completely free. View all properties and find your dream match.'
        }
      ]
    },
    {
      category: 'profile',
      categoryLabel: language === 'nl' ? 'Profiel & Voorkeuren' : 'Profile & Preferences',
      icon: User,
      questions: [
        {
          q: language === 'nl' ? 'Waarom moet ik een profiel invullen?' : 'Why do I need to fill out a profile?',
          a: language === 'nl'
            ? 'Je profiel (student, young professional, expat, etc.) helpt verhuurders te zien wie je bent. Een compleet profiel verhoogt je kansen op een match met wel 300%!'
            : 'Your profile (student, young professional, expat, etc.) helps landlords see who you are. A complete profile increases your match chances by up to 300%!'
        },
        {
          q: language === 'nl' ? 'Kan ik mijn voorkeuren wijzigen?' : 'Can I change my preferences?',
          a: language === 'nl'
            ? 'Ja, je kunt je voorkeuren (stad, budget, woningtype, huisdieren, inschrijving) op elk moment aanpassen via je profiel. Je krijgt dan direct nieuwe woningen te zien die bij je nieuwe criteria passen.'
            : 'Yes, you can adjust your preferences (city, budget, property type, pets, registration) at any time via your profile. You\'ll immediately see new properties matching your new criteria.'
        },
        {
          q: language === 'nl' ? 'Hoe kan ik contact opnemen met een verhuurder?' : 'How can I contact a landlord?',
          a: language === 'nl'
            ? 'Wanneer je een woning liked, wordt deze opgeslagen in je dashboard. Met een Premium abonnement kun je direct chatten met de verhuurder en je eigen bericht schrijven.'
            : 'When you like a property, it is saved in your dashboard. With a Premium subscription you can chat directly with the landlord and write your own message.'
        }
      ]
    },
    {
      category: 'landlords',
      categoryLabel: language === 'nl' ? 'Verhuurders' : 'Landlords',
      icon: Home,
      questions: [
        {
          q: language === 'nl' ? 'Wat kost het om te adverteren?' : 'What does it cost to advertise?',
          a: language === 'nl'
            ? 'Helemaal GRATIS! Je kunt onbeperkt woningen plaatsen zonder kosten. Wij verdienen alleen aan de huurders die Premium nemen.'
            : 'Completely FREE! You can post unlimited properties at no cost. We only earn from renters who take Premium.',
          popular: true
        },
        {
          q: language === 'nl' ? 'Hoe ontvang ik reacties?' : 'How do I receive responses?',
          a: language === 'nl'
            ? 'Wanneer iemand je woning liked, ontvang je hun profiel en automatische bericht in je dashboard. Je kunt de profielen bekijken en beslissen met wie je verder wilt chatten.'
            : 'When someone likes your property, you receive their profile and automatic message in your dashboard. You can view profiles and decide who you want to chat with.'
        },
        {
          q: language === 'nl' ? 'Kan ik voorkeuren instellen voor huurders?' : 'Can I set preferences for tenants?',
          a: language === 'nl'
            ? 'Ja! Je kunt aangeven of huisdieren zijn toegestaan, of inschrijving mogelijk is, het maximaal aantal bewoners en de minimale huurperiode.'
            : 'Yes! You can indicate if pets are allowed, if registration is possible, maximum number of occupants and minimum rental period.'
        }
      ]
    },
    {
      category: 'safety',
      categoryLabel: language === 'nl' ? 'Veiligheid' : 'Safety',
      icon: Shield,
      questions: [
        {
          q: language === 'nl' ? 'Zijn de advertenties betrouwbaar?' : 'Are the listings trustworthy?',
          a: language === 'nl'
            ? 'Ja! Alle advertenties worden handmatig gecontroleerd voordat ze online gaan. We verifiëren verhuurders en verwijderen verdachte advertenties direct. Veiligheid staat voorop.'
            : 'Yes! All listings are manually checked before going online. We verify landlords and remove suspicious listings immediately. Safety comes first.'
        },
        {
          q: language === 'nl' ? 'Hoe beschermen jullie mijn gegevens?' : 'How do you protect my data?',
          a: language === 'nl'
            ? 'Je gegevens zijn veilig bij ons. We delen nooit persoonlijke informatie zonder toestemming. Betalingen verlopen via beveiligde SSL-verbindingen.'
            : 'Your data is safe with us. We never share personal information without consent. Payments are processed via secure SSL connections.'
        },
        {
          q: language === 'nl' ? 'Wat moet ik doen bij een verdachte situatie?' : 'What should I do in a suspicious situation?',
          a: language === 'nl'
            ? 'Meld het direct via de "Rapporteer" knop of neem contact op met support@swipejewoning.nl. Betaal NOOIT vooraf zonder de woning te hebben gezien. Bij twijfel, niet doen!'
            : 'Report it immediately via the "Report" button or contact support@swipejewoning.nl. NEVER pay in advance without seeing the property. When in doubt, don\'t!'
        }
      ]
    }
  ];

  const filteredFaqs = faqs
    .filter(cat => activeCategory === 'all' || cat.category === activeCategory)
    .map(category => ({
      ...category,
      questions: category.questions.filter(
        q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) || 
             q.a.toLowerCase().includes(searchQuery.toLowerCase())
      )
    })).filter(category => category.questions.length > 0);

  const popularQuestions = faqs.flatMap(cat => 
    cat.questions.filter(q => q.popular).map(q => ({ ...q, category: cat.categoryLabel }))
  );

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F7F7]" data-testid="faq-page">
      <Header />
      
      <main className="flex-1 pt-20">
        {/* Hero Section */}
        <section className="relative py-20 px-4 overflow-hidden bg-gradient-to-br from-[#1F3A5F] to-[#0D2137]">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-[#2E7D32] rounded-full blur-[120px]"></div>
            <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-[#2E7D32]/50 rounded-full blur-[100px]"></div>
          </div>
          
          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#2E7D32] text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              <HelpCircle className="w-4 h-4" />
              {language === 'nl' ? 'HULP & SUPPORT' : 'HELP & SUPPORT'}
            </div>
            
            <h1 className="text-4xl md:text-5xl font-black text-white mb-6">
              {language === 'nl' ? 'Hoe kunnen we je helpen?' : 'How can we help you?'}
            </h1>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              {language === 'nl' 
                ? 'Vind snel antwoord op al je vragen over Swipe je woning'
                : 'Quickly find answers to all your questions about Swipe je woning'}
            </p>
            
            {/* Search Bar */}
            <div className="max-w-xl mx-auto relative">
              <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#9E9E9E]" />
              <Input
                type="text"
                placeholder={language === 'nl' ? 'Zoek een vraag...' : 'Search a question...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-14 h-14 bg-white border-0 text-[#2A2A2A] placeholder:text-[#9E9E9E] rounded-full shadow-xl text-lg"
              />
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-6 mt-10">
              {[
                { value: '320+', label: language === 'nl' ? 'Woningen' : 'Properties' },
                { value: '82%', label: language === 'nl' ? 'Match rate' : 'Match rate' },
                { value: '<2', label: language === 'nl' ? 'Weken gem.' : 'Weeks avg.' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-black text-[#2E7D32]">{stat.value}</div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="sticky top-20 z-30 bg-white border-b border-[#E5E5E5] shadow-sm">
          <div className="max-w-5xl mx-auto px-4 py-4">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                    activeCategory === cat.id
                      ? 'bg-[#2E7D32] text-white font-bold'
                      : 'bg-[#F7F7F7] text-[#2A2A2A] hover:bg-[#E5E5E5]'
                  }`}
                >
                  <cat.icon className="w-4 h-4" />
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Popular Questions */}
        {activeCategory === 'all' && !searchQuery && (
          <section className="py-12 px-4 bg-white">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-2 mb-6">
                <Zap className="w-5 h-5 text-[#2E7D32]" />
                <h2 className="text-xl font-black text-[#2A2A2A]">
                  {language === 'nl' ? 'Populaire vragen' : 'Popular questions'}
                </h2>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {popularQuestions.map((faq, i) => (
                  <div 
                    key={i}
                    className="bg-gradient-to-r from-[#2E7D32]/5 to-[#1F3A5F]/5 rounded-2xl p-5 border border-[#2E7D32]/20 hover:border-[#2E7D32]/40 transition-colors cursor-pointer"
                    onClick={() => {
                      const catIndex = faqs.findIndex(c => c.questions.some(q => q.q === faq.q));
                      const qIndex = faqs[catIndex].questions.findIndex(q => q.q === faq.q);
                      setExpandedIndex(`${catIndex}-${qIndex}`);
                      setActiveCategory(faqs[catIndex].category);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-[#2E7D32] rounded-full flex items-center justify-center flex-shrink-0">
                        <Star className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-bold text-[#2A2A2A] mb-1">{faq.q}</p>
                        <p className="text-sm text-[#9E9E9E]">{faq.category}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ List */}
        <section className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            {filteredFaqs.map((category, catIndex) => (
              <div key={catIndex} className="mb-10">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-[#2E7D32] rounded-xl flex items-center justify-center">
                    <category.icon className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-black text-[#2A2A2A]">
                    {category.categoryLabel}
                  </h2>
                </div>
                <div className="space-y-3">
                  {category.questions.map((faq, qIndex) => {
                    const globalIndex = `${catIndex}-${qIndex}`;
                    const isExpanded = expandedIndex === globalIndex;
                    return (
                      <div 
                        key={qIndex}
                        className={`bg-white rounded-2xl overflow-hidden border-2 transition-all ${
                          isExpanded ? 'border-[#2E7D32] shadow-lg' : 'border-[#E5E5E5] hover:border-[#2E7D32]/30'
                        }`}
                      >
                        <button
                          onClick={() => setExpandedIndex(isExpanded ? null : globalIndex)}
                          className="w-full flex items-center justify-between p-5 text-left"
                        >
                          <span className="font-bold text-[#2A2A2A] pr-4">{faq.q}</span>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                            isExpanded ? 'bg-[#2E7D32] rotate-180' : 'bg-[#F7F7F7]'
                          }`}>
                            <ChevronDown className={`w-5 h-5 ${isExpanded ? 'text-white' : 'text-[#2A2A2A]'}`} />
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="px-5 pb-5">
                            <div className="bg-[#F7F7F7] rounded-xl p-4 text-[#2A2A2A] leading-relaxed">
                              {faq.a}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {filteredFaqs.length === 0 && (
              <div className="text-center py-16 bg-white rounded-2xl border border-[#E5E5E5]">
                <Search className="w-12 h-12 text-[#9E9E9E] mx-auto mb-4" />
                <p className="text-[#9E9E9E] text-lg">
                  {language === 'nl' ? 'Geen resultaten gevonden.' : 'No results found.'}
                </p>
                <button 
                  onClick={() => { setSearchQuery(''); setActiveCategory('all'); }}
                  className="text-[#2E7D32] font-bold mt-2 hover:underline"
                >
                  {language === 'nl' ? 'Bekijk alle vragen' : 'View all questions'}
                </button>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-br from-[#2E7D32] to-[#1F3A5F]">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-bold mb-6">
              <Sparkles className="w-4 h-4" />
              {language === 'nl' ? 'KLAAR OM TE STARTEN?' : 'READY TO START?'}
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-6">
              {language === 'nl' 
                ? 'Vind je droomwoning voor €4,99/week'
                : 'Find your dream home for €4.99/week'}
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              {language === 'nl' 
                ? '82% van onze gebruikers vindt binnen 2 weken een woning. Jij kan de volgende zijn!'
                : '82% of our users find a home within 2 weeks. You could be next!'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/swipematch')}
                className="bg-white text-[#2E7D32] hover:bg-gray-100 h-14 px-8 text-lg font-bold rounded-full"
              >
                {language === 'nl' ? 'Start gratis met swipen' : 'Start swiping for free'}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={handleNavigateToPremium}
                disabled={navigatingToPremium}
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 h-14 px-8 text-lg font-bold rounded-full"
              >
                <Crown className="w-5 h-5 mr-2" />
                {language === 'nl' ? 'Bekijk Premium' : 'View Premium'}
              </Button>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 px-4 bg-white">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl font-black text-[#2A2A2A] mb-4">
                {language === 'nl' ? 'Vraag niet gevonden?' : 'Question not found?'}
              </h2>
              <p className="text-[#9E9E9E]">
                {language === 'nl' 
                  ? 'Neem contact met ons op en we helpen je binnen 24 uur verder.'
                  : 'Contact us and we\'ll help you within 24 hours.'}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <a 
                href="mailto:support@swipejewoning.nl"
                className="flex items-center gap-4 p-6 bg-[#F7F7F7] rounded-2xl hover:bg-[#E5E5E5] transition-colors group"
              >
                <div className="w-14 h-14 bg-[#2E7D32] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Mail className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="font-bold text-[#2A2A2A]">Email</p>
                  <p className="text-[#2E7D32]">support@swipejewoning.nl</p>
                </div>
              </a>
              
              <div className="flex items-center gap-4 p-6 bg-[#F7F7F7] rounded-2xl">
                <div className="w-14 h-14 bg-[#1F3A5F] rounded-xl flex items-center justify-center">
                  <Clock className="w-7 h-7 text-white" />
                </div>
                <div>
                  <p className="font-bold text-[#2A2A2A]">{language === 'nl' ? 'Reactietijd' : 'Response time'}</p>
                  <p className="text-[#9E9E9E]">{language === 'nl' ? 'Binnen 24 uur' : 'Within 24 hours'}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FAQPage;
